/**
 * Consolidation — groups pending sync events by entity,
 * producing one change per entity with only the changed fields.
 */

import { db } from '../database/schema.js'
import type { SyncEvent } from '../database/schema.js'
import { getBaseSnapshot } from './base-snapshot.js'
import { changedFields } from './three-way-merge.js'

export interface ConsolidatedChange {
  syncEventIds: string[]
  entityType: string
  entityId: string
  operation: 'create' | 'update' | 'delete'
  data: Record<string, unknown>
  expectedVersion: number
}

const ENTITY_TABLES: Record<string, string> = {
  route: 'routes',
  plan: 'plans',
  activity: 'activities',
  feature: 'features',
  layerGroup: 'layerGroups',
  edge: 'edges',
  team: 'teams',
  user: 'users',
}

export async function consolidateSyncEvents(): Promise<ConsolidatedChange[]> {
  const pending = await db.syncEvents.filter(e => !e.syncedAt).toArray()

  if (pending.length === 0) return []

  const grouped = new Map<string, SyncEvent[]>()
  for (const event of pending) {
    const key = `${event.entityType}:${event.entityId}`
    const group = grouped.get(key) ?? []
    group.push(event)
    grouped.set(key, group)
  }

  const changes: ConsolidatedChange[] = []

  for (const [, events] of grouped) {
    const sorted = events.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )

    const first = sorted[0]
    const last = sorted[sorted.length - 1]
    const syncEventIds = sorted.map(e => e.id)

    if (last.operation === 'delete') {
      let version = await getLocalVersion(first.entityType, first.entityId)
      if (version === 0) {
        version = (last.data?.version as number) ?? 0
      }
      changes.push({
        syncEventIds,
        entityType: first.entityType,
        entityId: first.entityId,
        operation: 'delete',
        data: {},
        expectedVersion: version,
      })
      continue
    }

    // The user entity is created server-side at signup and only ever pulled down,
    // so a client must never push it as a create — the backend rejects the duplicate
    // as a conflict forever (a stray 'create:user' event would loop indefinitely).
    // Force any user 'create' to fall through to the update path; LWW on the server
    // makes expectedVersion irrelevant, so it drains and syncs cleanly.
    if (first.operation === 'create' && first.entityType !== 'user') {
      const currentData = await getLocalEntityData(first.entityType, first.entityId)
      changes.push({
        syncEventIds,
        entityType: first.entityType,
        entityId: first.entityId,
        operation: 'create',
        data: currentData ?? last.data,
        expectedVersion: 0,
      })
      continue
    }

    const base = await getBaseSnapshot(first.entityType, first.entityId)
    const current = await getLocalEntityData(first.entityType, first.entityId)

    if (!current) continue

    const data = base ? changedFields(base, current) : current
    const version = await getLocalVersion(first.entityType, first.entityId)

    changes.push({
      syncEventIds,
      entityType: first.entityType,
      entityId: first.entityId,
      operation: 'update',
      data,
      expectedVersion: version,
    })
  }

  return changes
}

async function getLocalVersion(entityType: string, entityId: string): Promise<number> {
  const tableName = ENTITY_TABLES[entityType]
  if (!tableName) return 0
  const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
  if (!table) return 0
  const entity = await table.get(entityId)
  return ((entity as Record<string, unknown> | undefined)?.version as number) ?? 0
}

async function getLocalEntityData(
  entityType: string,
  entityId: string
): Promise<Record<string, unknown> | undefined> {
  const tableName = ENTITY_TABLES[entityType]
  if (!tableName) return undefined
  const table = (db as unknown as Record<string, import('dexie').Table>)[tableName]
  if (!table) return undefined
  const entity = await table.get(entityId)
  if (!entity) return undefined
  const data = { ...(entity as Record<string, unknown>) }
  delete data.accessStatus
  return data
}
